import { SKIP_AUTH_HEADER, request } from './request'
import { useServerFetch } from '../composables/useServerFetch'
import { useEnvApi } from '../composables/useEnvApi'

const SOFTWARE_DETAIL_API_PATH = '/api/v2/softwate/get_software_detail'
const RELEASE_NOTE_API_PATH = '/api/v2/softwate/get_release_note'
const COMMENT_INFO_API_PATH = '/api/v1/softwate/get_comment_base_info'
const COMMENT_LIST_API_PATH = '/api/v1/softwate/get_comment_list'
const DOWNLOAD_COUNT_API_PATH = '/api/v1/softwate/add_download_count'

export interface SoftwareDetailParams {
  marketId: string
  appBundleId: string
  softwareId: string
  traderCode: string
  companyId: string
  brandType: number
  deviceType: number | ''
  pageIndex: number
  pageSize: number
}

export interface ReleaseNoteParams {
  marketId: string
  appBundleId: string
  pageIndex: number
  pageSize: number
}

export interface CommentInfoParams {
  marketId: string
  appBundleId: string
  countryCode: string
}

export interface CommentListParams extends CommentInfoParams {
  commentType: string | number
  timeRangeType: string | number
  commentReplyType: string | number
  pageIndex: number
  pageSize: number
}

export interface SoftwareLabel {
  labelSource: number
  labelIcon: string
  labelName: string
}

export interface SoftwareCommentReply {
  replyTime?: string | number
  replyContent?: string
}

export interface SoftwareComment {
  id?: string | number
  commentId?: string | number
  title?: string
  starRating?: string | number
  starRatingText?: string
  countryCode?: string
  countryName?: string
  region?: string
  flag?: string
  commentTime?: string | number
  userName?: string
  content?: string
  replies?: SoftwareCommentReply[]
}

export interface SameTraderSoftware {
  marketId: number
  appBundleId: string
  softwareId: string
  logo: string
  name: string
  title: string
  isDown: boolean
  downloadNum: string
}

export interface SameTraderSoftwareGroup {
  items: SameTraderSoftware[]
  tabType: number
}

export interface SoftwareDetail {
  marketId: number
  appBundleId: string
  softwareId: string
  logo: string
  name: string
  title: string
  traderCode: string
  traderName: string
  status: number
  rating: number
  ratingCount: number
  sizes: string
  downloadNum: string
  compatibility: string
  shortCompatibility: string
  issueRegion: string
  shortIssueRegion: string
  screenshots: string[]
  description: string
  developer?: {
    name?: string
    logo?: string
    website?: string
  }
  releaseTime: string | number
  lastReleaseTime: string | number
  version: string
  languages: string
  shortLanguages: string
  supportWeb: string
  privacyPolicyUrl: string
  releaseNote: string
  sameTraderSoftwares: SameTraderSoftwareGroup[]
  isDown: boolean
  downLoadUrl: string
  softwareLabels: SoftwareLabel[]
  commentTotal: string
  commentPositive: string
  commentGood: string
  commentNeutral: string
  commentPoor: string
  commentNegative: string
  commentInfos: SoftwareComment[]
  activeValue: string
}

export interface ReleaseNote {
  releaseTime: string | number
  name: string
  version: string
  releaseNote: string
}

export interface ReleaseNoteData {
  total: number
  items: ReleaseNote[]
}

export interface CommentTabOption {
  tabType: string | number
  tabTypeText: string
}

export interface CommentCountryOption {
  countryCode: string
  countryName: string
  flag?: string
}

export interface CommentInfoData {
  rating: number
  commentTotal: string
  commentPositive: string
  commentGood: string
  commentNeutral: string
  commentPoor: string
  commentNegative: string
  commentTypes: CommentTabOption[]
  countryInfos: CommentCountryOption[]
  timeRangeTypes: CommentTabOption[]
  commentReplyTypes: CommentTabOption[]
}

export interface CommentListData {
  total: number
  items: SoftwareComment[]
}

interface SoftwareApiResponse<T> {
  code?: number
  data?: T
  result?: T
  Data?: T
  message?: string
  succeed?: boolean
}

/** 兼容客户端拦截器已解包及 SSR 原始响应两种返回结构。 */
const unwrapSoftwareApiResponse = <T>(response: SoftwareApiResponse<T> | T | null): T | null => {
  if (!response || typeof response !== 'object') return response ?? null

  const source = response as SoftwareApiResponse<T>
  if (source.result !== undefined) return source.result ?? null
  if (source.data !== undefined) {
    const nestedData = source.data
    if (nestedData && typeof nestedData === 'object' && 'result' in nestedData) {
      return (nestedData.result as T | null | undefined) ?? null
    }
    return nestedData ?? null
  }
  if (source.Data !== undefined) return source.Data ?? null

  return response as T
}

/** 软件接口均由 V4 网关提供，SSR 和客户端复用当前应用请求链路。 */
const requestSoftwareApi = async <T>(url: string, method: 'GET' | 'POST', payload: object): Promise<T | null> => {
  const api = useEnvApi()
  const requestConfig = {
    baseURL: api.v4,
    url,
    method,
    ...(method === 'GET' ? { params: payload } : { data: payload }),
    headers: {
      [SKIP_AUTH_HEADER]: '1',
    },
  }
  const response = import.meta.server
    ? await useServerFetch<SoftwareApiResponse<T>>(requestConfig)
    : await request<unknown, SoftwareApiResponse<T>>(requestConfig)

  return unwrapSoftwareApiResponse(response)
}

/** 获取交易应用详情。 */
export const getSoftwareDetail = (params: SoftwareDetailParams) =>
  requestSoftwareApi<SoftwareDetail>(SOFTWARE_DETAIL_API_PATH, 'GET', params)

/** 获取交易应用版本记录。 */
export const getSoftwareReleaseNotes = (params: ReleaseNoteParams) =>
  requestSoftwareApi<ReleaseNoteData>(RELEASE_NOTE_API_PATH, 'GET', params)

/** 获取评论数量及筛选项。 */
export const getSoftwareCommentInfo = (params: CommentInfoParams) =>
  requestSoftwareApi<CommentInfoData>(COMMENT_INFO_API_PATH, 'GET', params)

/** 根据筛选条件获取评论列表。 */
export const getSoftwareComments = (params: CommentListParams) =>
  requestSoftwareApi<CommentListData>(COMMENT_LIST_API_PATH, 'GET', params)

/** 记录应用下载按钮点击。 */
export const addSoftwareDownloadCount = (params: Pick<CommentInfoParams, 'marketId' | 'appBundleId'>) =>
  requestSoftwareApi<unknown>(DOWNLOAD_COUNT_API_PATH, 'POST', params)
